import { useQuery } from '@tanstack/react-query'
import { useOrgStore } from '@/stores/orgStore'
import { standardsApi } from '@/api/clients/standardsApi'
import { PageHeader, Card } from '@/components/common/ui'
import { Badge } from '@/components/common/Badge'
import { QueryState } from '@/components/common/State'

// Standards reference page — the compliance standards qtz-discovery ships (name,
// description, version, validity + migration windows, rule count). Read-only; discovery
// is the source of truth.
export default function Standards() {
  const { currentOrgId: orgId } = useOrgStore()
  const standards = useQuery({
    queryKey: ['standards', orgId],
    queryFn: () => standardsApi.list(orgId),
    enabled: !!orgId,
  })

  return (
    <div>
      <PageHeader
        title="Compliance standards"
        subtitle="PQC standards from discovery — validity + migration windows drive scoring urgency"
      />
      <QueryState
        isLoading={standards.isLoading}
        error={standards.error}
        isEmpty={(standards.data?.length ?? 0) === 0 && !standards.isLoading}
        emptyProps={{
          icon: 'rule',
          title: 'No standards available',
          hint: 'Standards come from qtz-discovery. Configure the discovery connection to see them.',
        }}
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {(standards.data ?? []).map((s) => (
            <Card key={s.name}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <div>
                  <h3 className="font-semibold text-text-light dark:text-text-dark">{s.name}</h3>
                  {s.version && (
                    <p className="text-xs text-subtle-light dark:text-subtle-dark">{s.version}</p>
                  )}
                </div>
                {typeof s.rule_count === 'number' && s.rule_count > 0 && (
                  <Badge label={`${s.rule_count} rules`} variant="neutral" />
                )}
              </div>

              {s.description && (
                <p className="text-sm text-subtle-light dark:text-subtle-dark mb-3">
                  {s.description}
                </p>
              )}

              <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                <Meta label="Effective from" value={s.effective_from} />
                <Meta label="Effective to" value={s.effective_to || 'in force'} />
                <Meta label="Migration start" value={s.migration_start} />
                <Meta label="Migration deadline" value={s.migration_deadline} />
              </dl>

              {s.aliases?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {s.aliases.map((a) => (
                    <span
                      key={a}
                      className="px-2 py-0.5 rounded-md text-[11px] font-mono bg-container-light dark:bg-container-dark text-subtle-light dark:text-subtle-dark"
                    >
                      {a}
                    </span>
                  ))}
                </div>
              )}
            </Card>
          ))}
        </div>
      </QueryState>
    </div>
  )
}

function Meta({ label, value }: { label: string; value?: string }) {
  return (
    <div>
      <dt className="text-subtle-light dark:text-subtle-dark">{label}</dt>
      <dd className="font-medium text-text-light dark:text-text-dark">{value || '—'}</dd>
    </div>
  )
}
